import * as React from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { ImagePlus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { BottlePreview } from "@/components/studio/BottlePreview"
import {
  LabelCanvas,
  ZOOM_STEP,
  clampZoom,
  useLabelDocument,
  type CanvasTool,
  type LabelCanvasHandle,
} from "@/components/studio/LabelCanvas"
import { loadBottleImages, type Assets } from "@/lib/label-canvas/bottles"
import type { FrameKind } from "@/lib/label-canvas/elements"
import { loadFonts } from "@/lib/label-canvas/fonts"
import type { OrnamentGroup, OrnamentKey } from "@/lib/label-canvas/ornaments"
import { bottleCompositeSize, renderBottlePng } from "@/lib/label-canvas/render"
import { DEFAULT_BACKGROUND, defaultDocument, imageElement, type BottleType } from "@/lib/label-canvas/schema"
import type { LabelTemplate } from "@/lib/label-canvas/templates"
import { cn } from "@/lib/utils"
import type { PageProps, StudioAi, StudioProps } from "@/types/inertia"
import { DescribeSheet, type ArtworkChoice } from "./DescribeSheet"
import { GateDialog } from "./GateDialog"
import { ImageUploadError, loadImage, prepareImage } from "./image-upload"
import {
  BackgroundPanel,
  BottlePanel,
  PropertiesPanel,
  ornamentWidthRange,
  type InspectorHandlers,
} from "./Inspector"
import { OrnamentMenu } from "./OrnamentMenu"
import { StudioSheet } from "./StudioSheet"
import { OwnArtworkSheet, TemplateGallery } from "./TemplateGallery"
import {
  DecorSegments,
  FRAME_CHOICES,
  MobileDock,
  MobilePills,
  ToolRail,
  type DecorSegment,
  type DockSheet,
} from "./Toolbars"

// The label studio editor (M4, extended in the W5 fix-up): tool rail · label
// well + live bottle preview · inspector on desktop; full-width canvas, a
// thumb-reach dock and bottom sheets on the phone. The canvas engine
// (LabelCanvas, useLabelDocument, renderBottlePng, templates, ornaments) is
// W1's and W5a's; this file wires it to the page: the template gallery, the
// ornament menu, the upload paths (image, template slot, own artwork),
// "Describe your design" (M5: a painted artwork plus seeded wording),
// persistence (POST/PATCH /mockups) and the download gate.

const DOWNLOAD_FILENAME = "soh-gd-wines-label-mockup.png"
const SMALL_PRINT = "Every mock-up is subject to graphic design approval by our team"
const TEMPLATE_HINT = "Every element is editable — tap any part of the label to change it."
const ARTWORK_HINT = "Your artwork fills the label. Move or resize it if needed."
const PAINTED_HINT = "Your artwork is on the label. Tap the wording to make it yours."
const PAINTED_HINT_NO_WORDING = "Your artwork is on the label. Tap Text to add your wording."
const HINT_MS = 6000
/** Production without an OpenAI key, or a render that predates the prop (the SSR smoke test). */
const AI_OFF: StudioAi = { enabled: false, styles: [] }

type Sheet = DockSheet | "edit" | "preview" | "ornaments" | "artwork" | "describe"
/**
 * A chosen "Describe your design" option not yet saved: the next save sends
 * artwork_generation_id + artwork_index instead of uploaded_image, and the
 * server copies that option's bytes into the mock-up's uploaded_image.
 */
type PendingArtwork = { generation_id: number; index: number }
type Busy = null | "download" | "save" | "signin"
type Status = { tone: "ok" | "error" | "info"; text: string } | null
/** What a gallery tile asks for; confirmed first when the label is not empty. */
type StartChoice = { kind: "template"; template: LabelTemplate } | { kind: "blank" }

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))
const round2 = (v: number) => Math.round(v * 100) / 100

function useIsDesktop() {
  const [desktop, setDesktop] = React.useState(false)
  React.useEffect(() => {
    const query = window.matchMedia("(min-width: 1024px)")
    const sync = () => setDesktop(query.matches)
    sync()
    query.addEventListener("change", sync)
    return () => query.removeEventListener("change", sync)
  }, [])
  return desktop
}

function errorText(errors: Record<string, string>) {
  return (
    errors.base ??
    errors.artwork ??
    errors.uploaded_image ??
    errors.rendered_preview ??
    errors.canvas_data ??
    errors.bottle_type ??
    Object.values(errors)[0] ??
    "Something went wrong. Please try again."
  )
}

class PersistError extends Error {}

export function StudioEditor() {
  const { props } = usePage<PageProps<StudioProps>>()
  const { mockup, bottles, prefill, gate } = props
  const ai = props.ai ?? AI_OFF
  const signedIn = Boolean(props.current_user)
  const selectionCount = props.selection?.bottle_count ?? 0

  const ld = useLabelDocument(mockup?.canvas_data ?? null, mockup?.bottle_type ?? prefill.bottle_type)
  const { doc } = ld

  const [tool, setTool] = React.useState<CanvasTool>("select")
  const [zoom, setZoom] = React.useState(1)
  const [assets, setAssets] = React.useState<Assets>({ bottleImages: {}, uploadedImage: null })
  const [pendingImage, setPendingImage] = React.useState<File | null>(null)
  const [pendingArtwork, setPendingArtwork] = React.useState<PendingArtwork | null>(null)
  const [mockupId, setMockupId] = React.useState<number | null>(mockup?.id ?? null)
  const [owned, setOwned] = React.useState(Boolean(mockup?.owned))
  const [busy, setBusy] = React.useState<Busy>(null)
  const [status, setStatus] = React.useState<Status>(null)
  const [sheet, setSheet] = React.useState<Sheet | null>(null)
  const [gateOpen, setGateOpen] = React.useState(false)
  const [confirmReset, setConfirmReset] = React.useState(false)
  const [editing, setEditing] = React.useState(false)
  const [pendingChoice, setPendingChoice] = React.useState<StartChoice | null>(null)
  const [hint, setHint] = React.useState<string | null>(null)
  // M6: after a guest's download, the saved design's id for the soft prompt
  // "Create an account to keep this design" (sign-up returns to it).
  const [keepId, setKeepId] = React.useState<number | null>(null)
  const [ornamentGroup, setOrnamentGroup] = React.useState<OrnamentGroup>("frames")
  const [decorSegment, setDecorSegment] = React.useState<DecorSegment>("frames")

  const isDesktop = useIsDesktop()
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const canvasRef = React.useRef<LabelCanvasHandle>(null)
  const uploadUrlRef = React.useRef<string | null>(null)
  const downloadUrlRef = React.useRef<string | null>(null)
  const hintTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const tapRef = React.useRef<{ x: number; y: number } | null>(null)
  const textFieldId = React.useId()

  // Latest values for handlers that run after an await or outside React's
  // own event (the file picker's change event, the canvas tap-through).
  const sheetRef = React.useRef(sheet)
  sheetRef.current = sheet
  const hasUpload = Boolean(assets.uploadedImage)
  const slotRef = React.useRef({ selected: ld.selected, hasUpload })
  slotRef.current = { selected: ld.selected, hasUpload }

  // ---- Assets: bottle PNGs, the saved upload, fonts (browser only) --------
  React.useEffect(() => {
    let live = true
    loadBottleImages().then((bottleImages) => {
      if (live) setAssets((a) => ({ ...a, bottleImages }))
    })
    void loadFonts(doc)
    return () => {
      live = false
    }
    // Once on mount; the canvas redraws itself when later fonts finish loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const savedImageUrl = mockup?.uploaded_image_url ?? null
  React.useEffect(() => {
    if (!savedImageUrl || pendingImage || pendingArtwork) return
    let live = true
    loadImage(savedImageUrl)
      .then((image) => {
        if (live) setAssets((a) => ({ ...a, uploadedImage: image }))
      })
      .catch(() => {
        if (live) setStatus({ tone: "error", text: "Your saved image could not be loaded." })
      })
    return () => {
      live = false
    }
    // Only when the saved image itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedImageUrl])

  React.useEffect(
    () => () => {
      if (uploadUrlRef.current) URL.revokeObjectURL(uploadUrlRef.current)
      if (downloadUrlRef.current) URL.revokeObjectURL(downloadUrlRef.current)
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current)
    },
    [],
  )

  // A one-line hint under the canvas after a template or artwork lands.
  const showHint = React.useCallback((text: string | null) => {
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current)
    hintTimerRef.current = null
    setHint(text)
    if (text) hintTimerRef.current = setTimeout(() => setHint(null), HINT_MS)
  }, [])

  // Cmd/Ctrl+Z undoes (the canvas leaves this to the page), except while typing.
  const undo = ld.undo
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.key.toLowerCase() !== "z") return
      const target = event.target as HTMLElement | null
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return
      event.preventDefault()
      undo()
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [undo])

  // ---- Element actions ---------------------------------------------------
  // A new line of text opens the in-place editor straight away (inside the
  // click handler, so a phone raises its keyboard).
  const addText = () => {
    const el = ld.addText()
    setTool("select")
    setSheet(null)
    canvasRef.current?.startEditing(el.id)
  }

  const addFrame = React.useCallback(
    (kind: FrameKind) => {
      ld.addFrame(kind)
      setSheet(null)
    },
    [ld],
  )

  const addOrnament = (key: OrnamentKey) => {
    ld.addOrnament(key)
    setTool("select")
    setSheet(null)
  }

  const pickImage = React.useCallback(() => fileInputRef.current?.click(), [])

  // One picker serves both lanes: with the "Add your finished artwork" sheet
  // open the file becomes edge-to-edge artwork, otherwise the label's image
  // (placed fresh, or filling a template's empty slot, or replacing the old one).
  const onFileChosen = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target
    const file = input.files?.[0]
    if (!file) return
    const artwork = sheetRef.current === "artwork"
    setStatus({ tone: "info", text: artwork ? "Preparing your artwork…" : "Preparing your image…" })
    try {
      const prepared = await prepareImage(file)
      if (uploadUrlRef.current) URL.revokeObjectURL(uploadUrlRef.current)
      uploadUrlRef.current = prepared.image.src
      setAssets((a) => ({ ...a, uploadedImage: prepared.image }))
      setPendingImage(prepared.file)
      // An upload replaces a painted artwork chosen earlier (it is the one image).
      setPendingArtwork(null)
      const natural = { width: prepared.width, height: prepared.height }
      if (artwork) {
        canvasRef.current?.stopEditing()
        ld.placeArtwork(natural)
        showHint(ARTWORK_HINT)
      } else {
        ld.placeImage(natural)
      }
      setTool("select")
      setSheet(null)
      setStatus(null)
    } catch (error) {
      console.error("[studio] image upload failed", error)
      setStatus({
        tone: "error",
        text: error instanceof ImageUploadError ? error.message : "That image could not be used. Please try another.",
      })
    } finally {
      // Clear so choosing the same file again still fires a change.
      input.value = ""
    }
  }

  // A tap on a template's empty image slot opens the picker. The canvas has
  // already selected what was tapped (on pointerdown), so a tap — not a drag —
  // that leaves an empty slot selected is a tap on the slot. Opening the
  // picker from the click keeps it inside the user gesture on every browser.
  const onSlotPointerDown = (event: React.PointerEvent) => {
    tapRef.current = event.isPrimary ? { x: event.clientX, y: event.clientY } : null
  }
  const onSlotClick = (event: React.MouseEvent) => {
    const start = tapRef.current
    tapRef.current = null
    if (!start || Math.hypot(event.clientX - start.x, event.clientY - start.y) > 6) return
    const { selected, hasUpload: uploaded } = slotRef.current
    if (selected?.type === "image" && !uploaded) pickImage()
  }
  const onSlotKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter" || event.defaultPrevented) return
    const { selected, hasUpload: uploaded } = slotRef.current
    if (selected?.type === "image" && !uploaded) {
      event.preventDefault()
      pickImage()
    }
  }

  // ---- Templates and own artwork ---------------------------------------------
  const applyChoice = (choice: StartChoice) => {
    setPendingChoice(null)
    setSheet(null)
    setTool("select")
    setStatus(null)
    canvasRef.current?.stopEditing()
    if (choice.kind === "blank") {
      showHint(null)
      // Already a blank warm-white label: nothing to replace, nothing to undo.
      if (doc.elements.length === 0 && doc.background.color === DEFAULT_BACKGROUND) return
      ld.applyTemplate(defaultDocument(doc.bottle))
      return
    }
    ld.applyTemplate(choice.template.build(doc.bottle))
    showHint(TEMPLATE_HINT)
  }

  const choose = (choice: StartChoice) => {
    if (ld.isEmpty) applyChoice(choice)
    else setPendingChoice(choice)
  }

  // ---- Describe your design (M5) -----------------------------------------------
  // "Use this" (the plan's useThisOption; named as a handler so it never reads
  // as a hook): the painted option becomes the label's one image, edge to edge,
  // with the wording seeded as editable text, in one undo entry — the own-
  // artwork behaviour, so no confirm (the sheet says Undo brings the old label
  // back). The option is a same-origin blob path, so the canvas stays clean
  // for export. Rejects (the sheet shows a sentence) if the image cannot load.
  const onUseThis = async ({ generationId, option, name, line }: ArtworkChoice) => {
    const image = await loadImage(option.url)
    // Cormorant must be measurable before the wording is fitted to one line.
    await loadFonts(null)
    canvasRef.current?.stopEditing()
    if (uploadUrlRef.current) URL.revokeObjectURL(uploadUrlRef.current)
    uploadUrlRef.current = null
    setAssets((a) => ({ ...a, uploadedImage: image }))
    setPendingImage(null)
    setPendingArtwork({ generation_id: generationId, index: option.index })
    ld.placeArtworkWithText({ width: image.naturalWidth, height: image.naturalHeight }, { name, line })
    setTool("select")
    setSheet(null)
    setStatus(null)
    showHint(name || line ? PAINTED_HINT : PAINTED_HINT_NO_WORDING)
  }

  const handlers: InspectorHandlers = {
    onBottle: (bottle: BottleType) => ld.setBottle(bottle),
    onStyle: ld.restyle,
    onText: (text) => {
      if (ld.selected?.type === "text") ld.update(ld.selected.id, { text }, { key: `text:${ld.selected.id}` })
    },
    onBackground: ld.setBackground,
    onDelete: () => {
      if (ld.selectedId) ld.remove(ld.selectedId)
      setSheet(null)
    },
    onPickImage: pickImage,
    // Resize about the ornament's centre, aspect kept, inside the label; a run
    // of − / + presses is one undo step.
    onOrnamentWidth: (width) => {
      const el = ld.selected
      if (el?.type !== "ornament") return
      const { aspect, min, max } = ornamentWidthRange(el, doc.label)
      const w = round2(clamp(width, min, max))
      const h = round2(w / aspect)
      const cx = el.x + el.width / 2
      const cy = el.y + el.height / 2
      ld.update(
        el.id,
        {
          width: w,
          height: h,
          x: round2(clamp(cx - w / 2, 0, doc.label.width_mm - w)),
          y: round2(clamp(cy - h / 2, 0, doc.label.height_mm - h)),
        },
        { key: `ornament-width:${el.id}` },
      )
    },
  }

  // ---- Persistence ---------------------------------------------------------
  const persist = React.useCallback(
    (renderedPreview: File | null) =>
      new Promise<PageProps<StudioProps>>((resolve, reject) => {
        const data: Record<string, string | File> = {
          bottle_type: doc.bottle,
          canvas_data: JSON.stringify(doc),
        }
        // The one image: a chosen painted option (its bytes are copied on the
        // server) or a fresh upload — never both (a later upload clears the option).
        if (pendingArtwork && imageElement(doc)) {
          data.artwork_generation_id = String(pendingArtwork.generation_id)
          data.artwork_index = String(pendingArtwork.index)
        } else if (pendingImage && imageElement(doc)) {
          data.uploaded_image = pendingImage
        }
        if (renderedPreview) data.rendered_preview = renderedPreview
        if (mockupId !== null) data._method = "patch"
        router.post(mockupId === null ? "/mockups" : `/mockups/${mockupId}`, data, {
          forceFormData: true,
          preserveState: true,
          preserveScroll: true,
          onSuccess: (page) => {
            const next = page.props as unknown as PageProps<StudioProps>
            if (!next.mockup) {
              reject(new PersistError("Your design could not be saved. Please try again."))
              return
            }
            setMockupId(next.mockup.id)
            setOwned(next.mockup.owned)
            setPendingImage(null)
            setPendingArtwork(null)
            resolve(next)
          },
          onError: (errors) => reject(new PersistError(errorText(errors as Record<string, string>))),
        })
      }),
    [doc, pendingImage, pendingArtwork, mockupId],
  )

  const renderPreview = React.useCallback(async () => {
    await loadFonts(doc)
    const blob = await renderBottlePng(doc, assets, bottles[doc.bottle])
    if (downloadUrlRef.current) URL.revokeObjectURL(downloadUrlRef.current)
    downloadUrlRef.current = URL.createObjectURL(blob)
    return new File([blob], "mockup-preview.png", { type: "image/png" })
  }, [doc, assets, bottles])

  const triggerDownload = React.useCallback(() => {
    const url = downloadUrlRef.current
    if (!url) return
    const a = document.createElement("a")
    a.href = url
    a.download = DOWNLOAD_FILENAME
    document.body.appendChild(a)
    a.click()
    a.remove()
  }, [])

  const fail = (error: unknown) =>
    setStatus({
      tone: "error",
      text: error instanceof Error && error.message ? error.message : "Something went wrong. Please try again.",
    })

  const download = async () => {
    if (busy) return
    setBusy("download")
    setStatus({ tone: "info", text: "Preparing your mock-up…" })
    try {
      const preview = await renderPreview()
      const saved = await persist(preview)
      const id = saved.mockup!.id
      if (gate.required) {
        setStatus(null)
        setSheet(null)
        setGateOpen(true)
      } else {
        // Signed in: no gate. The file downloads now; the lead + emailed copy follow.
        triggerDownload()
        const email = props.current_user?.email
        router.post(
          `/mockups/${id}/download`,
          {},
          {
            preserveState: true,
            preserveScroll: true,
            onSuccess: () =>
              setStatus({ tone: "ok", text: email ? `A copy is on its way to ${email}` : "Your mock-up has downloaded." }),
            onError: (errors) => setStatus({ tone: "error", text: errorText(errors as Record<string, string>) }),
          },
        )
      }
    } catch (error) {
      fail(error)
    } finally {
      setBusy(null)
    }
  }

  const save = async () => {
    if (busy) return
    setBusy(signedIn ? "save" : "signin")
    setStatus({ tone: "info", text: signedIn ? "Saving your design…" : "Saving your design before you sign in…" })
    try {
      const preview = await renderPreview()
      const saved = await persist(preview)
      if (signedIn) {
        // `owned` comes back from the server: a guest design saved after
        // signing in is only "in your account" once the server has claimed it.
        setStatus({ tone: "ok", text: saved.mockup!.owned ? "Saved to your account." : "Saved." })
      } else {
        router.visit(`/login?return_to=${encodeURIComponent(`/studio/${saved.mockup!.id}`)}`)
      }
    } catch (error) {
      fail(error)
    } finally {
      setBusy(null)
    }
  }

  const startAgain = () => {
    setConfirmReset(false)
    setSheet(null)
    setStatus(null)
    setKeepId(null)
    const bottle = doc.bottle
    if (mockupId !== null) {
      router.visit(`/studio?bottle=${bottle}`)
      return
    }
    ld.reset(null, bottle)
    setPendingImage(null)
    setPendingArtwork(null)
    setAssets((a) => ({ ...a, uploadedImage: null }))
    setZoom(1)
    showHint(null)
  }

  const onUnlocked = (email: string) => {
    triggerDownload()
    setGateOpen(false)
    setStatus({ tone: "ok", text: `A copy is on its way to ${email}` })
    setKeepId(mockupId)
  }

  // ---- Pieces --------------------------------------------------------------
  const bottleSpec = bottles[doc.bottle]
  // The desktop preview stage takes the composite's own aspect, so the bottle
  // fills the stage height (the stage's width is not left to its content).
  const previewSize = bottleCompositeSize(bottleSpec, 1)
  const previewLabel = `${doc.bottle === "mcc" ? "MCC" : "Wine"} bottle showing your label at true proportions`

  const statusLine = status && (
    <div
      className={cn(
        "studio-status",
        status.tone === "ok" && "studio-status-ok",
        status.tone === "error" && "studio-status-error",
      )}
      role={status.tone === "error" ? "alert" : "status"}
    >
      {status.text}
    </div>
  )

  // Invited, never forced (M6 decision 15): a guest who has just downloaded
  // can keep the design on an account; sign-up claims it and comes back here.
  const keepPrompt = !signedIn && keepId !== null && (
    <div className="studio-keep">
      <div className="field-label">Keep this design</div>
      <p>
        Create an account and this design waits for you in My designs, ready to open, duplicate or
        download again.
      </p>
      <Button asChild variant="ghost-hairline" size="sm">
        <Link href={`/signup?return_to=${encodeURIComponent(`/studio/${keepId}`)}`}>
          Create an account to keep this design
        </Link>
      </Button>
    </div>
  )

  const actions = (
    <section className="studio-panel" aria-label="Your mock-up">
      <div className="studio-panel-title">
        <div className="field-label">Your mock-up</div>
      </div>
      <div className="studio-actions">
        <Button type="button" variant="gold" size="lg" onClick={download} disabled={busy !== null}>
          {busy === "download" ? "Preparing…" : "Download mock-up"}
        </Button>
        {signedIn ? (
          <Button type="button" variant="ghost-hairline" size="sm" onClick={save} disabled={busy !== null}>
            {busy === "save" ? "Saving…" : owned ? "Save changes" : "Save to my account"}
          </Button>
        ) : (
          <Button type="button" variant="ghost-hairline" size="sm" onClick={save} disabled={busy !== null}>
            {busy === "signin" ? "Saving…" : "Sign in to save"}
          </Button>
        )}
        {statusLine}
        {keepPrompt}
        <div className="field-hint">{SMALL_PRINT}.</div>
        <button type="button" className="studio-quiet" onClick={() => setConfirmReset(true)}>
          Start again
        </button>
      </div>
    </section>
  )

  // The canvas's own accessible name summarises the design element by element.
  const canvas = (
    <LabelCanvas
      ref={canvasRef}
      {...ld.canvasProps}
      className="studio-canvas"
      assets={assets}
      tool={tool}
      onToolDone={() => setTool("select")}
      zoom={zoom}
      onZoom={(z) => setZoom(clampZoom(z))}
      onEditingChange={setEditing}
    />
  )

  const img = imageElement(doc)
  const imageButtonLabel = img && hasUpload ? "Replace image" : img?.placeholder?.trim() || "Upload an image"

  const frameTiles = (
    <div className="studio-sheet-grid">
      {FRAME_CHOICES.map(({ kind, label, Icon }) => (
        <button key={kind} type="button" className="studio-sheet-tile" onClick={() => addFrame(kind)}>
          <Icon aria-hidden="true" />
          {label}
        </button>
      ))}
    </div>
  )

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={onFileChosen}
      />

      <div className="studio-atelier">
        <ToolRail
          onTemplates={() => setSheet("templates")}
          onText={addText}
          onImage={pickImage}
          onOrnaments={() => setSheet("ornaments")}
          onFrame={addFrame}
          onUndo={ld.undo}
          canUndo={ld.canUndo}
          onClear={ld.clearAll}
          canClear={doc.elements.length > 0}
          zoom={zoom}
          onZoomIn={() => setZoom((z) => clampZoom(z * ZOOM_STEP))}
          onZoomOut={() => setZoom((z) => clampZoom(z / ZOOM_STEP))}
          onZoomFit={() => setZoom(1)}
          active={sheet}
        />

        <div className="studio-centre">
          <div className="studio-well">
            {/* Tap-through for a template's empty image slot (see onSlotClick). */}
            <div
              className="studio-canvas-slot"
              onPointerDown={onSlotPointerDown}
              onClick={onSlotClick}
              onKeyDown={onSlotKeyDown}
            >
              {canvas}
            </div>
            <span className={cn("studio-well-note", hint && "is-hidden")} aria-hidden="true">
              Front label · {bottleSpec ? `${doc.label.width_mm} × ${doc.label.height_mm} mm` : ""}
            </span>
            <div className="studio-hint-toast" role="status" aria-live="polite">
              {hint && <span>{hint}</span>}
            </div>
          </div>

          {!isDesktop && status && <div className="px-1">{statusLine}</div>}
          {!isDesktop && keepPrompt && <div className="px-1">{keepPrompt}</div>}

          {isDesktop && (
            <figure className="studio-preview">
              <div
                className="studio-preview-stage"
                style={{ aspectRatio: `${previewSize.width} / ${previewSize.height}` }}
              >
                <BottlePreview doc={doc} assets={assets} bottle={bottleSpec} ariaLabel={previewLabel} />
              </div>
              <figcaption className="studio-preview-notes">
                <div className="eyebrow">On the bottle</div>
                <h3>{doc.bottle === "mcc" ? "Your MCC bottle" : "Your wine bottle"}</h3>
                <p>Your label as it will sit on the glass, updated as you design.</p>
                <dl className="studio-preview-facts">
                  <div>
                    <dt>Front label</dt>
                    <dd>
                      {doc.label.width_mm} × {doc.label.height_mm} mm
                    </dd>
                  </div>
                  <div>
                    <dt>Bottle</dt>
                    <dd>About 30 cm tall</dd>
                  </div>
                </dl>
                <div className="studio-preview-caption">Shown at true label proportions · front label only</div>
              </figcaption>
            </figure>
          )}
        </div>

        <aside className="studio-inspector" aria-label="Label settings">
          <BottlePanel doc={doc} onBottle={handlers.onBottle} />
          <PropertiesPanel
            selected={ld.selected}
            style={ld.style}
            handlers={handlers}
            textFieldId={textFieldId}
            label={doc.label}
            hasUpload={hasUpload}
          />
          <BackgroundPanel doc={doc} onBackground={handlers.onBackground} />
          {actions}
        </aside>
      </div>

      {/* ---- Phone: dock, pills and sheets ---- */}
      {!editing && (
        <>
          <MobilePills
            hasSelection={ld.selected !== null}
            onEdit={() => setSheet("edit")}
            onPreview={() => setSheet("preview")}
          />
          <MobileDock
            onText={addText}
            onSheet={(next) => setSheet(next)}
            onUndo={ld.undo}
            canUndo={ld.canUndo}
            active={sheet}
          />
        </>
      )}
      <div className="studio-dock-spacer" aria-hidden="true" />

      {!isDesktop && (
        <>
          <StudioSheet open={sheet === "edit"} onOpenChange={(o) => !o && setSheet(null)} title="Edit">
            <PropertiesPanel
              selected={ld.selected}
              style={ld.style}
              handlers={handlers}
              textFieldId={`${textFieldId}-sheet`}
              label={doc.label}
              hasUpload={hasUpload}
            />
          </StudioSheet>

          <StudioSheet
            open={sheet === "image"}
            onOpenChange={(o) => !o && setSheet(null)}
            title="Image"
            description="One image per label — a logo, crest or photograph. PNG, JPEG or WebP, up to 10 MB."
          >
            <div className="studio-panel">
              <div className="studio-actions">
                <Button type="button" variant="gold" size="lg" onClick={pickImage}>
                  <ImagePlus className="mr-2 h-4 w-4" aria-hidden="true" />
                  {imageButtonLabel}
                </Button>
                {img && (
                  <button
                    type="button"
                    className="studio-danger justify-self-center"
                    onClick={() => {
                      ld.remove(img.id)
                      setSheet(null)
                    }}
                  >
                    <Trash2 aria-hidden="true" />
                    Remove image
                  </button>
                )}
              </div>
            </div>
          </StudioSheet>

          <StudioSheet
            open={sheet === "decor"}
            onOpenChange={(o) => !o && setSheet(null)}
            title="Frames & ornaments"
            description="A border, an oval or a divider — or a crest, laurel, ribbon or flourish."
          >
            <div className="studio-panel">
              <DecorSegments value={decorSegment} onChange={setDecorSegment} />
              <div className="studio-decor-body">
                {decorSegment === "frames" ? (
                  frameTiles
                ) : (
                  <OrnamentMenu
                    group={ornamentGroup}
                    onGroup={setOrnamentGroup}
                    color={ld.ornamentColor}
                    onAdd={addOrnament}
                  />
                )}
              </div>
            </div>
          </StudioSheet>

          <StudioSheet open={sheet === "more"} onOpenChange={(o) => !o && setSheet(null)} title="Your label">
            <BottlePanel doc={doc} onBottle={handlers.onBottle} />
            <BackgroundPanel doc={doc} onBackground={handlers.onBackground} title="Label colour" />
            <section className="studio-panel" aria-label="View and edit">
              <div className="studio-row">
                <Button
                  type="button"
                  variant="ghost-hairline"
                  size="sm"
                  onClick={() => setZoom(1)}
                  disabled={zoom === 1}
                >
                  Fit to screen
                </Button>
                <Button
                  type="button"
                  variant="ghost-hairline"
                  size="sm"
                  onClick={() => {
                    ld.clearAll()
                    setSheet(null)
                  }}
                  disabled={doc.elements.length === 0}
                >
                  Clear all
                </Button>
              </div>
            </section>
            {actions}
            {selectionCount > 0 && (
              <section className="studio-panel" aria-label="Your selection">
                <Link href="/order">
                  Review your selection · {selectionCount} {selectionCount === 1 ? "bottle" : "bottles"}
                </Link>
              </section>
            )}
          </StudioSheet>

          <StudioSheet
            open={sheet === "preview"}
            onOpenChange={(o) => !o && setSheet(null)}
            title="Preview"
            description="Shown at true label proportions · front label only"
          >
            <div className="studio-sheet-preview">
              <BottlePreview doc={doc} assets={assets} bottle={bottleSpec} ariaLabel={previewLabel} />
            </div>
          </StudioSheet>
        </>
      )}

      {/* ---- Both layouts: gallery, artwork, ornaments (desktop dialog) ---- */}
      <TemplateGallery
        open={sheet === "templates"}
        onOpenChange={(o) => !o && setSheet(null)}
        bottle={doc.bottle}
        onBlank={() => choose({ kind: "blank" })}
        onOwnArtwork={() => setSheet("artwork")}
        onDescribe={ai.enabled ? () => setSheet("describe") : undefined}
        onChoose={(template) => choose({ kind: "template", template })}
      />

      <OwnArtworkSheet
        open={sheet === "artwork"}
        onOpenChange={(o) => !o && setSheet(null)}
        onChoose={pickImage}
        replaces={!ld.isEmpty}
      />

      {ai.enabled && (
        <DescribeSheet
          open={sheet === "describe"}
          onOpenChange={(o) => !o && setSheet(null)}
          styles={ai.styles}
          bottle={doc.bottle}
          replaces={!ld.isEmpty}
          onUse={onUseThis}
        />
      )}

      {isDesktop && (
        <StudioSheet
          open={sheet === "ornaments"}
          onOpenChange={(o) => !o && setSheet(null)}
          title="Ornaments"
          description="Crests, laurels, ribbons and flourishes. Add one at a time, then move, resize and recolour it."
        >
          <div className="studio-panel">
            <OrnamentMenu
              group={ornamentGroup}
              onGroup={setOrnamentGroup}
              color={ld.ornamentColor}
              onAdd={addOrnament}
            />
          </div>
        </StudioSheet>
      )}

      <Dialog open={pendingChoice !== null} onOpenChange={(o) => !o && setPendingChoice(null)}>
        <DialogContent className="studio-modal" size="sm">
          <DialogHeader>
            <DialogTitle>Replace your design?</DialogTitle>
            <DialogDescription>Your current label will be replaced. Undo brings it back.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3">
            <Button type="button" variant="ghost-hairline" size="sm" onClick={() => setPendingChoice(null)}>
              Keep mine
            </Button>
            <Button
              type="button"
              variant="gold"
              size="sm"
              onClick={() => pendingChoice && applyChoice(pendingChoice)}
            >
              Replace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GateDialog open={gateOpen} onOpenChange={setGateOpen} mockupId={mockupId} onUnlocked={onUnlocked} />

      <Dialog open={confirmReset} onOpenChange={setConfirmReset}>
        <DialogContent className="studio-modal" size="sm">
          <DialogHeader>
            <DialogTitle>Start again?</DialogTitle>
            <DialogDescription>
              This clears the label and begins a fresh design. {mockupId !== null && "Your saved mock-up is kept."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3">
            <Button type="button" variant="ghost-hairline" size="sm" onClick={() => setConfirmReset(false)}>
              Keep designing
            </Button>
            <Button type="button" variant="gold" size="sm" onClick={startAgain}>
              Start again
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
